import { useState } from 'react'
import type { FormEvent } from 'react'
import {
  ApiError, changeEmail, changePassword, revokeOtherSessions,
  type Me, type Session,
} from '../api'
import { useApi } from '../useApi'
import { Empty, ErrorBox, Field, Rows } from '../components/bits'
import { PageHead } from '../components/Shell'
import { useT } from '../lang'

/* Your own account: the password, the address, and where you are signed in.
 *
 * The session list is the reason this page is worth having. GET
 * /v1/auth/sessions has existed since the first day there were sessions and
 * has never had anywhere to appear, which meant the only way to find out that
 * somebody else was signed in as you was to already know.
 */

function why(err: unknown): string {
  if (!(err instanceof ApiError)) return 'Could not reach the receiver.'
  switch (err.code) {
    case 'weak_password':
      return `That password will not do: ${err.detail ?? 'choose another'}.`
    case 'invalid_credentials':
      return 'That is not your current password.'
    default:
      return err.detail ?? err.code
  }
}

export function Account({ me }: { me: Me }) {
  const t = useT()
  return (
    <>
      <PageHead
        title={t('Your account')}
        lead={me.email ?? me.label}
      />
      <Password />
      <EmailAddress current={me.email} />
      <Sessions />
    </>
  )
}

/* ---------------------------------------------------------------- password */

function Password() {
  const t = useT()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [again, setAgain] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await changePassword(current, next)
      setDone(true)
      setCurrent(''); setNext(''); setAgain('')
    } catch (err) {
      setError(why(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="card stack" onSubmit={submit} style={{ gap: 16 }}>
      <h2 style={{ margin: 0, fontSize: 18 }}>{t('Password')}</h2>

      {/* Asked for even though you are already signed in. It is what stops an
          unattended logged-in laptop from being a permanent account takeover
          rather than a temporary one. */}
      <Field label="Current password">
        <input type="password" value={current} autoComplete="current-password"
          required onChange={(e) => setCurrent(e.target.value)} />
      </Field>
      <Field label="New password"
        hint={t('At least 12 characters. Length beats punctuation.')}>
        <input type="password" value={next} autoComplete="new-password"
          required minLength={12} onChange={(e) => setNext(e.target.value)} />
      </Field>
      <Field label="Repeat it">
        <input type="password" value={again} autoComplete="new-password"
          required onChange={(e) => setAgain(e.target.value)} />
      </Field>

      {again.length > 0 && next !== again &&
        <div className="notice bad">{t('The two do not match.')}</div>}
      {error && <div className="notice bad">{error}</div>}
      {done && <div className="notice">
        {t('Changed. Every other device has been signed out.')}
      </div>}

      <div>
        <button className="btn primary" type="submit"
          disabled={busy || !current || !next || next !== again}>
          {busy ? t('Saving…') : t('Change password')}
        </button>
      </div>
    </form>
  )
}

/* ------------------------------------------------------------------ email */

function EmailAddress({ current }: { current: string | null }) {
  const t = useT()
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await changeEmail(email)
      setSent(true)
    } catch (err) {
      setError(why(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="card stack" onSubmit={submit} style={{ gap: 16 }}>
      <h2 style={{ margin: 0, fontSize: 18 }}>{t('Email address')}</h2>
      <p className="muted" style={{ margin: 0, fontSize: 14 }}>{current}</p>

      <Field label="New address"
        hint={t('Nothing changes until you confirm it from the new address.')}>
        <input type="email" value={email} autoComplete="email"
          onChange={(e) => setEmail(e.target.value)} />
      </Field>

      {error && <div className="notice bad">{error}</div>}
      {sent && <div className="notice">
        {/* Says "if it is free" rather than confirming either way: the same
            reason the login form has one failure message. A signed-in page is
            still a way to ask whether an address has an account here. */}
        {t('If that address is free, a confirmation link is on its way to it. The old address has been told.')}
      </div>}

      <div>
        <button className="btn" type="submit" disabled={busy || !email}>
          {busy ? t('Sending…') : t('Change address')}
        </button>
      </div>
    </form>
  )
}

/* --------------------------------------------------------------- sessions */

function Sessions() {
  const t = useT()
  const { data, error, cold, reload } =
    useApi<{ items: Session[] }>('/v1/auth/sessions')
  const [busy, setBusy] = useState(false)

  const signOutOthers = async () => {
    setBusy(true)
    try {
      await revokeOtherSessions()
      reload()
    } finally {
      setBusy(false)
    }
  }

  const items: Session[] = data?.items ?? []

  return (
    <div className="card stack" style={{ gap: 16 }}>
      <h2 style={{ margin: 0, fontSize: 18 }}>{t('Where you are signed in')}</h2>

      {error && <ErrorBox error={error} />}
      {cold && <Rows n={2} />}

      {!cold && items.length === 0 && (
        <Empty title={t('Nothing to show')}>
          {t('This is the only signed-in session.')}
        </Empty>
      )}

      {items.length > 0 && (
        <table className="tbl">
          <thead>
            <tr>
              <th>{t('Last seen')}</th>
              <th>{t('Signed in')}</th>
              <th>{t('Address')}</th>
              <th>{t('Browser')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((s, i) => (
              <tr key={i}>
                <td>{new Date(s.last_seen_at).toLocaleString()}</td>
                <td>{new Date(s.created_at).toLocaleString()}</td>
                <td className="muted">{s.ip ?? '—'}</td>
                {/* Whole, not prettified into a browser name. A user-agent you
                    do not recognise is the entire signal this table carries,
                    and shortening it is how the unfamiliar one starts looking
                    like the familiar one. */}
                <td className="faint" style={{ fontSize: 12, wordBreak: 'break-all' }}>
                  {s.user_agent || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {items.length > 1 && (
        <div>
          <button className="btn" onClick={() => void signOutOthers()} disabled={busy}>
            {busy ? t('Signing out…') : t('Sign out everywhere else')}
          </button>
        </div>
      )}
    </div>
  )
}
